import QuickBooksSourceBanner from './QuickBooksSourceBanner';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import WarningIcon from '@mui/icons-material/Warning';
import quickbooksService, {
  formatCurrency,
  formatCurrencyPrecise,
  formatCompactCurrency,
  formatDate,
  getEntityColor,
} from '../../services/quickbooksService';

export default function CashPosition() {
  const [loading, setLoading] = useState(true);
  const [liveLoading, setLiveLoading] = useState(false);
  const [error, setError] = useState(null);
  const [entities, setEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState('all');
  const [cashPosition, setCashPosition] = useState(null);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [dataSource, setDataSource] = useState('cached'); // 'cached' or 'live'
  const [dataFreshness, setDataFreshness] = useState(null);

  useEffect(() => {
    loadData();
    loadDataFreshness();
  }, [selectedEntity]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      setDataSource('cached');

      const params = selectedEntity !== 'all' ? { connector_id: selectedEntity } : {};

      const [entitiesRes, cashRes, bsRes] = await Promise.all([
        quickbooksService.getQuickBooksEntities(),
        quickbooksService.getCashPosition(params),
        quickbooksService.getBalanceSheetSummary(params),
      ]);

      if (entitiesRes.success) setEntities(entitiesRes.data);
      if (cashRes.success) setCashPosition(cashRes.data);
      if (bsRes.success) setBalanceSheet(bsRes.data);

    } catch (err) {
      console.error('Error loading cash position:', err);
      setError('Failed to load cash position data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadDataFreshness = async () => {
    try {
      const freshnessRes = await quickbooksService.getDataFreshness();
      if (freshnessRes.success) {
        setDataFreshness(freshnessRes.data);
      }
    } catch (err) {
      console.error('Error loading data freshness:', err);
    }
  };

  const loadLiveData = async () => {
    try {
      setLiveLoading(true);
      setError(null);

      const params = selectedEntity !== 'all' ? { connector_id: selectedEntity } : {};

      const liveRes = await quickbooksService.getLiveBankBalances(params);

      if (liveRes.success) {
        setCashPosition({
          total: liveRes.data.total,
          by_entity: liveRes.data.by_entity,
          all_accounts: liveRes.data.accounts
        });
        setDataSource('live');

        if (liveRes.data.errors && liveRes.data.errors.length > 0) {
          setError(`Some entities could not be fetched: ${liveRes.data.errors.map(e => e.entity_name).join(', ')}`);
        }
      }
    } catch (err) {
      console.error('Error loading live cash position:', err);
      setError('Failed to fetch live data from QuickBooks. Check if tokens are valid.');
    } finally {
      setLiveLoading(false);
    }
  };

  const handleEntityChange = (event) => {
    setSelectedEntity(event.target.value);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const totalCash = cashPosition?.total || 0;
  const isDataStale = dataFreshness?.recommendation?.status === 'stale';

  return (
    <Box sx={{ p: 3 }}>
      <QuickBooksSourceBanner />
      {/* Error Alert */}
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Data Freshness Warning */}
      {isDataStale && dataSource === 'cached' && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          icon={<WarningIcon />}
          action={
            <Tooltip title="Fetch live data directly from QuickBooks API">
              <IconButton
                color="inherit"
                size="small"
                onClick={loadLiveData}
                disabled={liveLoading}
              >
                {liveLoading ? <CircularProgress size={20} color="inherit" /> : <CloudSyncIcon />}
              </IconButton>
            </Tooltip>
          }
        >
          Data is {dataFreshness?.recommendation?.days_old} days old. Click the sync icon to fetch live data from QuickBooks.
        </Alert>
      )}

      {/* Live Data Indicator */}
      {dataSource === 'live' && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Showing live data fetched directly from QuickBooks API
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight={600}>
            Cash Position
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Cash and bank account balances across all entities
            {dataSource === 'live' && ' (Live from QuickBooks)'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Entity</InputLabel>
            <Select
              value={selectedEntity}
              label="Entity"
              onChange={handleEntityChange}
            >
              <MenuItem value="all">All Entities</MenuItem>
              {entities.map((entity) => (
                <MenuItem key={entity.connector_id} value={entity.connector_id}>
                  {entity.company_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Fetch Live Data from QuickBooks">
            <IconButton
              onClick={loadLiveData}
              color="success"
              disabled={liveLoading}
            >
              {liveLoading ? <CircularProgress size={24} /> : <CloudSyncIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh Cached Data">
            <IconButton onClick={loadData} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Total Cash
                  </Typography>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {formatCompactCurrency(totalCash)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {cashPosition?.all_accounts?.length || 0} accounts
                  </Typography>
                </Box>
                <Box sx={{
                  backgroundColor: 'primary.light',
                  borderRadius: '50%',
                  p: 1.5,
                }}>
                  <AccountBalanceWalletIcon sx={{ color: 'primary.main' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Total Assets
                  </Typography>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 600, color: 'success.main' }}>
                    {formatCompactCurrency(balanceSheet?.total_assets || 0)}
                  </Typography>
                </Box>
                <Box sx={{
                  backgroundColor: 'success.light',
                  borderRadius: '50%',
                  p: 1.5,
                }}>
                  <TrendingUpIcon sx={{ color: 'success.main' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Total Liabilities
                  </Typography>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 600, color: 'warning.main' }}>
                    {formatCompactCurrency(balanceSheet?.total_liabilities || 0)}
                  </Typography>
                </Box>
                <Box sx={{
                  backgroundColor: 'warning.light',
                  borderRadius: '50%',
                  p: 1.5,
                }}>
                  <TrendingDownIcon sx={{ color: 'warning.main' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Net Equity
                  </Typography>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 600, color: 'info.main' }}>
                    {formatCompactCurrency(balanceSheet?.net_equity || 0)}
                  </Typography>
                </Box>
                <Box sx={{
                  backgroundColor: 'info.light',
                  borderRadius: '50%',
                  p: 1.5,
                }}>
                  <AccountBalanceIcon sx={{ color: 'info.main' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="By Entity" />
          <Tab label="By Account" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Cash by Entity
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Entity</TableCell>
                    <TableCell align="right">Bank Accounts</TableCell>
                    <TableCell align="right">Cash Balance</TableCell>
                    <TableCell align="right">% of Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cashPosition?.by_entity?.map((entity, index) => {
                    const entityName = entity.entity_name || entity.company_name || `Entity ${entity.connector_id}`;
                    return (
                      <TableRow key={entity.connector_id || index}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: getEntityColor(entityName)
                              }}
                            />
                            <Typography fontWeight={500}>
                              {entityName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">{entity.accounts?.length || entity.account_count || 0}</TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={600}>
                            {formatCurrencyPrecise(entity.total)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${totalCash > 0 ? ((entity.total / totalCash) * 100).toFixed(1) : 0}%`}
                            size="small"
                            sx={{
                              backgroundColor: getEntityColor(entityName),
                              color: 'white',
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {(!cashPosition?.by_entity || cashPosition.by_entity.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No cash position data available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Bank Account Details
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Account Name</TableCell>
                    <TableCell>Entity</TableCell>
                    <TableCell>Account Type</TableCell>
                    <TableCell>Currency</TableCell>
                    <TableCell align="right">Balance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cashPosition?.all_accounts?.map((account, index) => {
                    const entityInfo = entities.find(e => e.connector_id === account.connector_id);
                    const entityName = entityInfo?.company_name || `Entity ${account.connector_id}`;
                    return (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography fontWeight={500}>
                            {account.account_name || account.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={entityName}
                            size="small"
                            sx={{
                              backgroundColor: getEntityColor(entityName),
                              color: 'white',
                            }}
                          />
                        </TableCell>
                        <TableCell>{account.account_type || 'Bank'}</TableCell>
                        <TableCell>{account.currency_code || 'GBP'}</TableCell>
                        <TableCell align="right">
                          <Typography
                            fontWeight={600}
                            color={(account.current_balance || 0) >= 0 ? 'success.main' : 'error.main'}
                          >
                            {formatCurrencyPrecise(account.current_balance, account.currency_code || 'GBP')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {(!cashPosition?.all_accounts || cashPosition.all_accounts.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No bank accounts found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
